import CoreButton from "@components/CoreButton"
import ErrorIcon from '@images/icons/error.svg?react'
import { withTranslation } from 'react-i18next'
import { ErrorBoundary } from 'react-error-boundary'
import { GlobalContextProvider, isValidObject, OtpContext } from 'mmfintech-commons'
import { Route, Switch } from 'react-router-dom'
import Modal from '@components/ModalDialog'
import { useDispatch } from 'react-redux'

import { CookiesProvider } from 'react-cookie'
import { Suspense, useEffect, useRef, useState } from "react"
import { routes } from './routes'
import { PublicRoute } from "./utils"
import '@scss/app/app.scss'
import { prepareCardGatewayUrl, useAppSelector } from "mmfintech-backend-api"
import { ThemeProvider } from 'styled-components'
import theme from './theme'

import axios from 'axios'
import cn from "classnames"
import { CustomPreloader } from '@components/CustomPreloader'

function AppInner() {

  const [modalContent, setModalContent] = useState(null)
  const [modalHeader, setModalHeader] = useState(null)
  const [modalOptions, setModalOptions] = useState(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [otpOnSuccess, setOtpOnSuccess] = useState(null)
  const [otpOnError, setOtpOnError] = useState(null)
  const contentRef = useRef(null)
  const [parentMessage, setParentMessage] = useState(null)
  const [pendingAction, setPendingAction] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const dispatch = useDispatch()
  const queryChallenge = useAppSelector(state => state.challenge)

  const modalHide = () => {
    setModalVisible(false)
    setModalContent(null)
    setModalOptions(null)
    setModalHeader(null)
    window.parent.postMessage({ type: 'HIDE_OVERLAY' }, '*')
  }
  const modalShow = ({ options, content, header }) => {
    setModalContent(content)
    setModalOptions(options)
    setModalHeader(header)
    setModalVisible(true)
  }

  const globalContext = {
    modalHide,
    modalShow,
    parentMessage,
    pendingAction,
    setPendingAction
  }

  const allowedOrigins = [
    'http://localhost:3000',
    'https://portal-dev.jetonbank.com',
    'https://portal-sandbox.jetonbank.com',
    'https://portal.jetonbank.com'
  ]

  useEffect(() => {
    let observer = null

    const messageHandler = async (event) => {
      // Check the origin of the data!
      if (allowedOrigins.includes(event.origin)) {

        const message = event.data

        switch (message.type) {
          case 'HANDSHAKE':
            await axios({
              url: prepareCardGatewayUrl('/v1/auth/init'),
              method: 'POST',
              withCredentials: true,
              data: message.value
            })
            break
          case 'DEPOSIT_REQUEST':
            setParentMessage(message.value)
            break
          case 'WITHDRAWAL_REQUEST':
            setParentMessage(message.value)
            break
          case 'SHOW_CARD_DETAILS':
            setParentMessage(message.value)
            setPendingAction(message.type)
            break
          case 'REVEAL_DETAILS':
            setParentMessage(message.value)
            setPendingAction(message.type)
            break
          case 'MODAL_STATE':
            const wasModalOpen = isModalOpen
            const newModalState = message.value === 'OPENED'
            setIsModalOpen(newModalState)
            
            if (contentRef?.current) {
              const targetNode = contentRef.current
              
              if (newModalState && observer) {
                // Pause observer when modal opens
                observer.disconnect()
              } else if (!newModalState && !observer && wasModalOpen) {
                // Resume observer when modal closes
                const handleResize = resize => {
                  if (!isModalOpen) {
                    //@ts-ignore
                    window.parent.postMessage({ type: 'IFRAME_SIZE', value: resize?.[0]?.target?.clientHeight }, '*')
                  }
                }
                observer = new ResizeObserver(handleResize)
                observer.observe(targetNode)
              }
            }
            break
        }
      }
      // The data was NOT sent from your site!
      // Be careful! Do not use it.
    }

    window.addEventListener('message', messageHandler);

    if (contentRef?.current) {
      const targetNode = contentRef.current

      const handleResize = resize => {
        if (!isModalOpen) {
          //@ts-ignore
          window.parent.postMessage({ type: 'IFRAME_SIZE', value: resize?.[0]?.target?.clientHeight }, '*')
        }
      }
      observer = new ResizeObserver(handleResize)
      observer.observe(targetNode)
    }

    return () => {
      window.removeEventListener('message', messageHandler);
      observer?.disconnect()
    }
  }, [isModalOpen])

  useEffect(() => {
    if (isValidObject(queryChallenge) && queryChallenge?.hasOwnProperty('challengeId')) {
      // modalShow({
      //   header: prepareTitle({ twoFactorType, userStatus }),
      //   options: { closeOnClickOutside: false },
      //   content: <Otp />
      // })

    }
  }, [queryChallenge, dispatch])

  return (
    <CookiesProvider>
      <ThemeProvider theme={theme}>
        <GlobalContextProvider context={globalContext}>
          <OtpContext.Provider
            value={{
              otpOnSuccess,
              setOtpOnSuccess,
              otpOnError,
              setOtpOnError
            }}>
            <div className={cn('content-container', { 'only_modal': modalVisible })} ref={contentRef}>
              <Switch>
                {routes.map(({ path, PrivateComponent }, index) => (
                  <PublicRoute
                    key={index}
                    path={path}
                    exact
                    component={PrivateComponent}
                  />
                ))}
                <Route path='*' component={null} />
              </Switch>

              <Modal
                header={modalHeader}
                content={modalContent}
                options={modalOptions}
                visible={modalVisible}
                onClose={modalHide}
              />
            </div>
          </OtpContext.Provider>
        </GlobalContextProvider>
      </ThemeProvider>
    </CookiesProvider>
  )
}



function ErrorFallback({ resetErrorBoundary }) {
  return (
    <div className='app-alert-wrapper'>
      <div className='app-alert'>
        <ErrorIcon />
        <p data-test='app-alert-unexpected-error'>An unexpected error occurred</p>
        <CoreButton
          fullWidth
          data-test='app-alert-unexpected-error-back-to-home-button'
          variation='primary'
          size='normal'
          style={{ maxWidth: '30rem' }}
          onClick={resetErrorBoundary}
          title='Go back to the home page'
        />
      </div>
    </div>
  )
}

const ThisApp = withTranslation()(AppInner)

function App() {
  return (
    <Suspense
      fallback={
        <div className='app-preload-wrapper'>
          <CustomPreloader />
        </div>
      }>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(err, { componentStack }) => console.error({ level: 'ERROR', componentStack, message: err.toString() })}
        onReset={() => {
          window.location.replace('/')
        }}>
        <ThisApp />
      </ErrorBoundary>
    </Suspense>

  )
}

export default App
